/**
 * Shared module-level state for the test-only deprecation capture flow.
 *
 * Consumer apps register a deprecation handler (via an
 * `app/initializers/deprecations.js` initializer) that throws on every
 * unexpected deprecation so regressions are caught loudly. Tests that
 * exercise intentionally-deprecated APIs need to intercept the
 * deprecation instead, but layering a second `registerDeprecationHandler`
 * does not work because `setupTest`/`setupRenderingTest` rebuild the
 * test owner per test, which re-runs the deprecations initializer and
 * stacks another throwing handler at the front of the chain.
 *
 * Instead, the initializer cooperates with the test helper via the
 * module-level `captureCallback` held here. The test helper
 * (`test-support/setup-deprecation-capture`) installs and clears it per
 * test; the initializer reads it back through
 * `getDeprecationCaptureCallback()` and forwards the deprecation when
 * one is active.
 *
 * The state lives in `utils/` (not `test-support/`) so the production
 * initializer can import the getter without tripping the
 * `ember/no-test-support-import` lint rule. Outside of a test
 * `captureCallback` is always `null`, so the production cost is one
 * trivial getter call per deprecation.
 */

let captureCallback = null;

export function getDeprecationCaptureCallback() {
  return captureCallback;
}

/**
 * Test-only setter consumed by
 * `test-support/setup-deprecation-capture`. Production code MUST NOT
 * call this — leaving the throw-on-deprecation default in place is
 * intentional.
 *
 * @param {((message: string, options: object) => void) | null} callback
 */
export function setDeprecationCaptureCallback(callback) {
  captureCallback = callback;
}
